import React, { useState, useEffect } from "react";
import {
  Box, Input, InputGroup, Button, Text, Spinner, HStack, Image, Checkbox,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Link,
} from "@chakra-ui/react";
import AppleLogo from "assets/svg/apple.svg";
import GoogleLogo from "assets/svg/google.svg";
import { getPrivacyPolicyOrTerms } from "auth/gridAuthClient";

const FIELD_W = "260px";
const inputSt = {
  bg: "transparent", border: "1px solid #fff", borderRadius: "10px",
  color: "#fff", fontSize: "13px", p: "10px", w: "100%"
};

export default function SocialSignupModal({ firebaseUser, socialType, onComplete, onCancel }) {
  const [state, setState] = useState({
    username: "", error: "", isLoading: false, isPoliciesChecked: false,
    showPolicyDialog: false, policyLoading: false, policyType: ""
  });
  const [policyContent, setPolicyContent] = useState(null);

  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  useEffect(() => {
    if (!firebaseUser) return;
    const defaultUsername = firebaseUser.displayName
      ? firebaseUser.displayName.replace(/\s+/g, '').toLowerCase().replace(/[^a-z0-9]/g, '')
      : firebaseUser.email?.split('@')[0] || "";
    updateState({ username: defaultUsername });
  }, [firebaseUser]);

  const validateUsername = () => {
    const { username, isPoliciesChecked } = state;
    if (!username || username.trim().length < 3) return "Username must be at least 3 characters long.";
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return "Username can only contain letters, numbers, and underscores.";
    if (!isPoliciesChecked) return "You must agree to the terms of service and privacy policy.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    updateState({ error: "" });
    
    const validationError = validateUsername();
    if (validationError) return updateState({ error: validationError });

    updateState({ isLoading: true });
    try {
      await onComplete(state.username.trim());
    } catch (err) {
      updateState({ error: err.message || "Failed to complete signup. Please try again.", isLoading: false });
    }
  };

  const handleCancel = () => {
    setState({ username: "", error: "", isLoading: false, isPoliciesChecked: false,
      showPolicyDialog: false, policyLoading: false, policyType: "" });
    onCancel();
  };

  const handlePolicyClick = async (type) => {
    updateState({ policyType: type, policyLoading: true, showPolicyDialog: true });
    
    try {
      const response = await getPrivacyPolicyOrTerms(type);
      const content = response?.response || response || {};
      setPolicyContent({
        title: content.title || (type === "privacy policy" ? "Privacy Policy" : "Terms & Conditions"),
        description: content.description || "No content available.",
        modifiedMonth: content.modifiedMonth,
        modifiedDay: content.modifiedDay,
        modifiedYear: content.modifiedYear
      });
    } catch (err) {
      setPolicyContent({ title: "Error", description: "Failed to load content. Please try again later." });
    } finally {
      updateState({ policyLoading: false });
    }
  };

  const formatDate = () => {
    if (!policyContent?.modifiedMonth || !policyContent?.modifiedDay || !policyContent?.modifiedYear) return "";
    return `Last Updated: ${policyContent.modifiedMonth} ${policyContent.modifiedDay}, ${policyContent.modifiedYear}`;
  };

  if (!firebaseUser) return null;

  const PolicyLink = ({ type, label }) => (
    <Link color="#fff" textDecoration="underline" onClick={(e) => {
      e.preventDefault(); handlePolicyClick(type);
    }} cursor="pointer">{label}</Link>
  );

  return (
    <>
      <Box position="fixed" top="0" left="0" right="0" bottom="0" bg="rgba(0,0,0,0.8)"
        zIndex="9998" onClick={handleCancel} />
    
      <Box position="fixed" top="50%" left="50%" transform="translate(-50%, -50%)" w="320px" p="20px"
        bg="rgba(255,255,255,0.1)" border="1px solid #fff" borderRadius="10px"
        boxShadow="0 4px 6px rgba(0,0,0,0.1)" backdropFilter="blur(5px)"
        fontFamily="Poppins, sans-serif" zIndex="9999">
        
        <Box as="form" onSubmit={handleSubmit} display="flex" flexDirection="column" alignItems="center" px="25px">
          <HStack spacing="8px" mb="6px">
            <Image src={socialType === 'google' ? GoogleLogo : AppleLogo} alt={socialType}
              boxSize={socialType === 'google' ? "20px" : "22px"} />
            <Text color="#fff" fontSize="20px">Complete Sign Up</Text>
          </HStack>
          
          <Text color="#ADA8C3" fontSize="12px" mb="20px" textAlign="center">
            Choose a username to complete your {socialType === 'google' ? 'Google' : 'Apple'} account
          </Text>

          <Box w={FIELD_W} mb="15px" textAlign="center">
            <Text fontSize="13px" color="#ADA8C3">{firebaseUser.email}</Text>
            {firebaseUser.displayName && (
              <Text fontSize="13px" color="#ADA8C3">{firebaseUser.displayName}</Text>
            )}
          </Box>

          <Box w={FIELD_W} mb="4px">
            <InputGroup>
              <Input {...inputSt} type="text" placeholder="Username *" required
                value={state.username} onChange={e => updateState({ username: e.target.value })}
                disabled={state.isLoading} autoFocus />
            </InputGroup>
          </Box>
          
          <Text color="#ADA8C3" fontSize="10px" mb="12px" textAlign="center">
            Username cannot contain spaces.
          </Text>

          <HStack w={FIELD_W} mb="12px" align="flex-start">
            <Checkbox isChecked={state.isPoliciesChecked}
              onChange={(e) => updateState({ isPoliciesChecked: e.target.checked })}
              borderColor="#fff"
              sx={{ '& .chakra-checkbox__control': { borderColor: '#fff', bg: 'transparent',
                _checked: { bg: '#fff', borderColor: '#fff', color: '#000' } } }} />
            <Text fontSize="10px" color="#ADA8C3">
              I agree to the <PolicyLink type="terms conditions" label="terms of service" />
              {" "}and <PolicyLink type="privacy policy" label="privacy policy" />
            </Text>
          </HStack>

          {state.error && (
            <Text color="red" fontSize="12px" mb="12px" textAlign="center" fontWeight="bold">
              {state.error}
            </Text>
          )}

          {state.isLoading ? (
            <Spinner color="white" mt="10px" mb="10px" w="30px" h="30px" thickness="4px" speed="0.65s" />
          ) : (
            <>
              <Button type="submit" w={FIELD_W} mt="10px" p="10px" bg="#fff" color="#000"
                borderRadius="10px" fontSize="13px"
                _hover={{ cursor: "pointer", bg: "transparent", color: "#fff", outline: "1px solid #fff" }}
                disabled={!state.username.trim() || !state.isPoliciesChecked}>
                Complete Sign Up
              </Button>
              
              <Button onClick={handleCancel} w={FIELD_W} mt="10px" p="10px" bg="transparent"
                color="#fff" border="1px solid #fff" borderRadius="10px" fontSize="13px"
                _hover={{ cursor: "pointer", bg: "#fff", color: "#000" }}>
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Modal isOpen={state.showPolicyDialog} onClose={() => updateState({ showPolicyDialog: false })}
        size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="rgba(255,255,255,0.95)" border="1px solid #fff" borderRadius="10px"
          maxH="80vh" color="#000">
          <ModalHeader>
            {state.policyLoading ? <Spinner size="sm" /> : (
              <Box>
                <Text fontSize="18px" fontWeight="bold">{policyContent?.title || state.policyType}</Text>
                {policyContent && <Text fontSize="12px" color="gray.600" mt="4px">{formatDate()}</Text>}
              </Box>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb="20px">
            {state.policyLoading ? (
              <Box textAlign="center" py="20px">
                <Spinner />
                <Text mt="10px" color="gray.600">Loading...</Text>
              </Box>
            ) : (
              <Box dangerouslySetInnerHTML={{ __html: policyContent?.description || "No content available." }}
                sx={{ '& p': { marginBottom: '12px' }, '& h1, & h2, & h3': { marginTop: '16px', marginBottom: '8px', fontWeight: 'bold' },
                  '& ul, & ol': { paddingLeft: '20px', marginBottom: '12px' }, '& li': { marginBottom: '4px' },
                  '& a': { color: 'blue.500', textDecoration: 'underline' } }} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}